import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  DocumentTextIcon,
  AdjustmentsHorizontalIcon,
  CalendarIcon,
  PlayIcon,
  CreditCardIcon,
  ChartBarIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { payrollService } from '../../../services/accounting/payroll';
import { formatCurrency, formatDate } from '../../../utils/formatting';
import toast from 'react-hot-toast';
import CreatePayrollPeriodModal from '../modals/CreatePayrollPeriodModal';
import ProcessPayrollModal from '../modals/ProcessPayrollModal';
import PayrollPaymentModal from '../modals/PayrollPaymentModal';
import PayrollAdjustmentModal from '../modals/PayrollAdjustmentModal';
import PayrollDetailModal from '../modals/PayrollDetailModal';
import EmployeeManagementModal from '../modals/EmployeeManagementModal';
import AddEmployeeModal from '../modals/AddEmployeeModal';

/**
 * PayrollTab Component
 * 
 * Comprehensive payroll management interface that handles:
 * - Employee/Driver listing with payroll information
 * - Payroll period management and processing
 * - Salary and commission calculations
 * - Payment processing and tracking
 * - Audit trail and reporting
 * 
 * Features:
 * - Automated commission calculation from shipments
 * - Full accounting integration with journal entries
 * - Batch payment processing
 * - Adjustment tracking and audit logs
 * - Role-based access control
 * 
 * Translation Support:
 * - All user-facing text uses i18n translation keys
 * - Supports both Arabic and English interfaces
 * - Maintains RTL compatibility
 */
const PayrollTab = () => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [payrollEntries, setPayrollEntries] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [employeeStatusFilter, setEmployeeStatusFilter] = useState('active'); // active, inactive, all
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, employees, periods, entries
  
  // Modal states
  const [showCreatePeriodModal, setShowCreatePeriodModal] = useState(false);
  const [showProcessPayrollModal, setShowProcessPayrollModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEmployeeManagementModal, setShowEmployeeManagementModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);


  /**
   * Load employees data
   */
  const loadEmployees = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    try {
      setLoading(true);
      console.log('🔄 Loading employees for company:', currentCompany.id);
      const employeesData = await payrollService.getEmployees(currentCompany.id);
      console.log('👥 Employees loaded:', employeesData.length, employeesData);
      setEmployees(employeesData);
    } catch (error) {
      console.error('❌ Error loading employees:', error);
      toast.error(t('payroll.error.loading_employees', 'Failed to load employees'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, t]);

  /**
   * Load payroll periods
   */
  const loadPayrollPeriods = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    try {
      const periodsData = await payrollService.getPayrollPeriods(currentCompany.id);
      setPayrollPeriods(periodsData);
      
      // Auto-select the most recent period
      if (periodsData.length > 0 && !selectedPeriod) {
        setSelectedPeriod(periodsData[0]);
      }
    } catch (error) {
      console.error('Error loading payroll periods:', error);
      toast.error(t('payroll.error.loading_periods', 'Failed to load payroll periods'));
    }
  }, [currentCompany?.id, selectedPeriod, t]);

  /**
   * Load payroll entries for selected period
   */
  const loadPayrollEntries = useCallback(async () => {
    if (!currentCompany?.id || !selectedPeriod?.id) return;
    
    try {
      const entriesData = await payrollService.getPayrollEntries(currentCompany.id, selectedPeriod.id);
      setPayrollEntries(entriesData);
    } catch (error) {
      console.error('Error loading payroll entries:', error);
      toast.error(t('payroll.error.loading_entries', 'Failed to load payroll entries'));
    }
  }, [currentCompany?.id, selectedPeriod?.id, t]);

  // Load data on component mount and dependencies change
  useEffect(() => {
    loadEmployees();
    loadPayrollPeriods();
  }, [loadEmployees, loadPayrollPeriods]);

  useEffect(() => {
    loadPayrollEntries();
  }, [loadPayrollEntries]);

  /**
   * Handle payroll period creation
   */
  const handlePeriodCreated = () => {
    setShowCreatePeriodModal(false);
    loadPayrollPeriods();
  };

  /**
   * Handle payroll processing
   */
  const handlePayrollProcessed = () => {
    setShowProcessPayrollModal(false);
    loadPayrollPeriods();
    loadPayrollEntries();
  };

  /**
   * Handle payment processing
   */
  const handlePaymentProcessed = () => {
    setShowPaymentModal(false);
    setSelectedEntries([]);
    loadPayrollEntries();
  };

  /**
   * Handle payroll adjustment
   */
  const handleAdjustmentMade = () => {
    setShowAdjustmentModal(false);
    setSelectedEntry(null);
    loadPayrollEntries();
  };

  /**
   * Handle employee management updates
   */
  const handleEmployeeUpdated = () => {
    setShowEmployeeManagementModal(false);
    setSelectedEmployee(null);
    loadEmployees();
  };



  /**
   * Get status badge component
   */
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { class: 'badge text-white bg-slate-600', text: t('payroll.status.draft', 'Draft') },
      accrued: { class: 'badge text-white bg-blue-600', text: t('payroll.status.accrued', 'Accrued') },
      paid: { class: 'badge text-white bg-green-600', text: t('payroll.status.paid', t('common.paid')) },
      partial: { class: 'badge text-white bg-orange-600', text: t('payroll.status.partial', 'Partially Paid') },
      cancelled: { class: 'badge text-white bg-red-600', text: t('payroll.status.cancelled', 'Cancelled') }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return <span className={config.class}>{config.text}</span>;
  };

  /**
   * Get payment status badge
   */
  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      accrued: { class: 'badge text-white bg-amber-600', text: t('payroll.status.accrued') },
      paid: { class: 'badge text-white bg-green-600', text: t('accounting.filter.paid') },
      cancelled: { class: 'badge text-white bg-red-600', text: t('accounting.cancelled') }
    };
    
    const config = statusConfig[status] || statusConfig.accrued;
    return <span className={config.class}>{config.text}</span>;
  };

  // Filter entries based on search and status
  const filteredEntries = payrollEntries.filter(entry => {
    const matchesSearch = 
      entry.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.employeeRole.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || entry.paymentStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate summary statistics
  const summary = {
    totalEmployees: employees.filter(emp => emp.status === 'active').length,
    totalSalaries: selectedPeriod ? (selectedPeriod.totalSalaries || 0) : 
      payrollEntries.reduce((sum, entry) => sum + (entry.salary || 0), 0),
    totalCommissions: selectedPeriod ? (selectedPeriod.totalCommissions || 0) : 
      payrollEntries.reduce((sum, entry) => sum + (entry.commission || 0), 0),
    totalPayable: selectedPeriod ? (selectedPeriod.totalPayable || 0) : 
      payrollEntries.reduce((sum, entry) => sum + (entry.totalDue || 0), 0),
    totalPaid: payrollEntries.filter(e => e.paymentStatus === 'paid').reduce((sum, entry) => sum + (entry.totalDue || 0), 0),
    unpaidCount: payrollEntries.filter(e => e.paymentStatus === 'accrued').length
  };

  /**
   * Handle period status change
   */
  const handlePeriodStatusChange = async (periodId, newStatus) => {
    try {
      await payrollService.updatePayrollPeriodStatus(periodId, newStatus, currentUser?.uid || 'system');
      toast.success(t('payroll.period_status_updated', 'Period status updated successfully'));
      loadPayrollPeriods();
    } catch (error) {
      console.error('Error updating period status:', error);
      toast.error(t('payroll.error.status_update_failed', 'Failed to update period status'));
    }
  };

  /**
   * Handle period deletion
   */
  const handleDeletePeriod = async (periodId) => {
    const period = payrollPeriods.find(p => p.id === periodId);
    const periodName = period ? `${period.year}-${String(period.month).padStart(2, '0')}` : periodId;
    
    if (window.confirm(t('payroll.confirm_delete_period', 'Are you sure you want to delete this payroll period? This action cannot be undone.') + `\n\n${t('payroll.period', 'Period')}: ${periodName}`)) {
      try {
        await payrollService.deletePayrollPeriod(periodId);
        toast.success(t('payroll.period_deleted', 'Payroll period deleted successfully'));
        loadPayrollPeriods();
        // Reset selected period if it was deleted
        if (selectedPeriod?.id === periodId) {
          setSelectedPeriod(null);
        }
      } catch (error) {
        console.error('Error deleting period:', error);
        let errorMessage = t('payroll.error.delete_failed', 'Failed to delete period');
        
        if (error.message.includes('existing payroll entries')) {
          errorMessage = t('payroll.error.cannot_delete_with_entries', 'Cannot delete period with existing payroll entries. Please remove all entries first.');
        } else if (error.message.includes('not found')) {
          errorMessage = t('payroll.error.period_not_found', 'Payroll period not found.');
        }
        
        toast.error(errorMessage);
      }
    }
  };

  /**
   * Handle view period details
   */
  const handleViewPeriod = async (period) => {
    setSelectedPeriod(period);
    setActiveView('entries');
    await loadPayrollEntries();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body text-center">
            <UserGroupIcon className="w-8 h-8 mx-auto text-blue-600 mb-2" />
            <div className="text-2xl font-bold text-gray-900">{summary.totalEmployees}</div>
            <div className="text-sm text-gray-500">{t('payroll.total_employees', 'Total Employees')}</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <CurrencyDollarIcon className="w-8 h-8 mx-auto text-green-600 mb-2" />
            <div className="text-2xl font-bold text-green-600">{formatCurrency(summary.totalPayable)}</div>
            <div className="text-sm text-gray-500">{t('payroll.total_payable', 'Total Payable')}</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <CheckCircleIcon className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(summary.totalPaid)}</div>
            <div className="text-sm text-gray-500">{t('payroll.total_paid', 'Total Paid')}</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <ClockIcon className="w-8 h-8 mx-auto text-amber-600 mb-2" />
            <div className="text-2xl font-bold text-amber-600">{summary.unpaidCount}</div>
            <div className="text-sm text-gray-500">{t('payroll.unpaid_entries', 'Unpaid Entries')}</div>
          </div>
        </div>
      </div>

      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('payroll.manage_payroll', 'Manage Payroll')}</h2>
          <p className="text-sm text-gray-500">{t('payroll.description', 'Process salaries, commissions, and employee payments')}</p>
        </div>
        
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <button 
            onClick={() => setShowAddEmployeeModal(true)}
            className="btn btn-success"
          >
            <UserGroupIcon className="w-4 h-4 mr-2" />
            {t('payroll.add_employee', 'Add Employee')}
          </button>
          
          <button 
            onClick={() => setShowCreatePeriodModal(true)}
            className="btn btn-secondary"
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            {t('payroll.create_period', 'Create Period')}
          </button>
          
          {selectedPeriod && (selectedPeriod.status === 'draft' || selectedPeriod.status === 'accrued') && (
            <button 
              onClick={() => {
                if (selectedPeriod) {
                  setShowProcessPayrollModal(true);
                }
              }}
              className="btn btn-primary"
            >
              <PlayIcon className="w-4 h-4 mr-2" />
              {selectedPeriod.status === 'draft' 
                ? t('payroll.process_payroll', 'Process Payroll')
                : t('payroll.process_salary', 'Process Salary')
              }
            </button>
          )}
          
          {selectedEntries.length > 0 && (
            <button 
              onClick={() => setShowPaymentModal(true)}
              className="btn btn-success"
            >
              <CreditCardIcon className="w-4 h-4 mr-2" />
              {t('payroll.process_payment', 'Process Payment')}
            </button>
          )}
        </div>
      </div>

      {/* View Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {[
            { id: 'dashboard', name: t('payroll.dashboard', t('common.dashboard')), icon: ChartBarIcon },
            { id: 'employees', name: t('payroll.employees', 'Employees'), icon: UserGroupIcon },
            { id: 'periods', name: t('payroll.periods', 'Periods'), icon: CalendarIcon },
            { id: 'entries', name: t('payroll.entries', 'Payroll Entries'), icon: DocumentTextIcon }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                className={`
                  group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium
                  ${activeView === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
              >
                <Icon
                  className={`
                    -ml-0.5 mr-2 h-5 w-5
                    ${activeView === tab.id
                      ? 'text-blue-500'
                      : 'text-gray-400 group-hover:text-gray-500'
                    }
                  `}
                  aria-hidden="true"
                />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Period Selection */}
      {payrollPeriods.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-blue-900">
                {t('payroll.current_period', 'Current Period')}
              </h3>
              <p className="text-sm text-blue-700">
                {selectedPeriod ? (
                  <span className="flex items-center space-x-2">
                    <span>{selectedPeriod.year}-{String(selectedPeriod.month).padStart(2, '0')}</span>
                    {getStatusBadge(selectedPeriod.status)}
                    <span className="text-xs">
                      ({selectedPeriod.employeeCount || 0} {t('payroll.employees', 'employees')})
                    </span>
                  </span>
                ) : (
                  t('payroll.no_period_selected', 'No period selected')
                )}
              </p>
            </div>
            <select
              value={selectedPeriod?.id || ''}
              onChange={(e) => {
                const period = payrollPeriods.find(p => p.id === e.target.value);
                setSelectedPeriod(period);
              }}
              className="form-select"
            >
              <option value="">{t('payroll.select_period', 'Select Period')}</option>
              {payrollPeriods.map((period, index) => (
                <option key={`period-${period.id}-${index}`} value={period.id}>
                  {period.year}-{String(period.month).padStart(2, '0')} ({t(`payroll.status.${period.status}`, period.status)})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Content based on active view */}
      {activeView === 'dashboard' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activity */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium">{t('payroll.recent_activity', 'Recent Activity')}</h3>
            </div>
            <div className="card-body">
              {payrollPeriods.slice(0, 5).map((period, index) => (
                <div key={`recent-${period.id}-${index}`} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <div className="font-medium">{period.year}-{String(period.month).padStart(2, '0')}</div>
                    <div className="text-sm text-gray-500">{formatCurrency(period.totalPayable || 0)}</div>
                  </div>
                  {getStatusBadge(period.status)}
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium">{t('payroll.quick_stats', 'Quick Stats')}</h3>
            </div>
            <div className="card-body space-y-4">
              <div className="flex justify-between">
                <span>{t('payroll.total_salaries', 'Total Salaries')}</span>
                <span className="font-medium">{formatCurrency(summary.totalSalaries)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('payroll.total_commissions', 'Total Commissions')}</span>
                <span className="font-medium">{formatCurrency(summary.totalCommissions)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('payroll.pending_payments', 'Pending Payments')}</span>
                <span className="font-medium text-amber-600">{summary.unpaidCount}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeView === 'employees' && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">{t('payroll.employees_list', 'Employees List')}</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowAddEmployeeModal(true)}
                  className="btn btn-sm btn-success"
                >
                  <UserGroupIcon className="w-4 h-4 mr-2" />
                  {t('payroll.add_employee', 'Add Employee')}
                </button>
                
                <select
                  value={employeeStatusFilter}
                  onChange={(e) => setEmployeeStatusFilter(e.target.value)}
                  className="form-select text-sm"
                >
                  <option value="active">{t('employees.active_only', 'Active Only')}</option>
                  <option value="inactive">{t('employees.inactive_only', 'Inactive Only')}</option>
                  <option value="all">{t('common.all_statuses', 'All Statuses')}</option>
                </select>
                
                <div className="relative">
                  <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('common.search', 'Search...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          const filteredEmployees = employees.filter(emp => {
                            const statusMatch = employeeStatusFilter === 'all' || emp.status === employeeStatusFilter;
                            const searchMatch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                              emp.role.toLowerCase().includes(searchTerm.toLowerCase());
                            return statusMatch && searchMatch;
                          });
                          
                          if (e.target.checked) {
                            setSelectedEmployees(filteredEmployees.map(emp => emp.id));
                          } else {
                            setSelectedEmployees([]);
                          }
                        }}
                        className="form-checkbox"
                      />
                    </th>
                    <th>{t('payroll.employee_name', 'Employee Name')}</th>
                    <th>{t('payroll.employee_id', 'Employee ID')}</th>
                    <th>{t('payroll.role', 'Role')}</th>
                    <th>{t('payroll.type', 'Type')}</th>
                    <th>{t('payroll.salary', 'Salary')}</th>
                    <th>{t('payroll.commission_eligible', 'Commission Eligible')}</th>
                    <th>{t('common.status', t('common.status'))}</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.filter(emp => {
                    // Status filter
                    const statusMatch = employeeStatusFilter === 'all' || emp.status === employeeStatusFilter;
                    
                    // Search filter
                    const searchMatch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                      emp.role.toLowerCase().includes(searchTerm.toLowerCase());
                    
                    return statusMatch && searchMatch;
                  }).map((employee, index) => (
                    <tr 
                      key={`employee-${employee.id}-${index}`}
                      className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedEmployees.includes(employee.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedEmployees.includes(employee.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEmployees(prev => [...prev, employee.id]);
                            } else {
                              setSelectedEmployees(prev => prev.filter(id => id !== employee.id));
                            }
                          }}
                          className="form-checkbox"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td 
                        className="font-medium"
                        onClick={() => {
                          console.log('🖱️ Employee row clicked:', employee.name, employee);
                          setSelectedEmployee(employee);
                          setShowEmployeeManagementModal(true);
                          console.log('📋 Modal should open now');
                        }}
                      >
                        {employee.name}
                      </td>
                      <td>{employee.employeeId}</td>
                      <td>{employee.role}</td>
                      <td>
                        <span className={`badge ${employee.type === 'driver' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {employee.type === 'driver' ? t('payroll.driver', 'Driver') : t('payroll.employee', 'Employee')}
                        </span>
                      </td>
                      <td>{formatCurrency(employee.salary || 0)}</td>
                      <td>
                        {employee.commissionEligible ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${employee.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {employee.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Selection Summary */}
            {selectedEmployees.length > 0 && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  {t('payroll.selection_summary', 'Selection Summary')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span>{t('payroll.selected_employees', 'Selected Employees')}:</span>
                    <span className="font-medium">{selectedEmployees.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.total_salaries', 'Total Salaries')}:</span>
                    <span className="font-medium">
                      {formatCurrency(
                        employees
                          .filter(emp => selectedEmployees.includes(emp.id))
                          .reduce((sum, emp) => sum + (parseFloat(emp.salary) || 0), 0)
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.commission_eligible', 'Commission Eligible')}:</span>
                    <span className="font-medium">
                      {employees
                        .filter(emp => selectedEmployees.includes(emp.id) && emp.commissionEligible)
                        .length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('payroll.active_employees', 'Active Employees')}:</span>
                    <span className="font-medium">
                      {employees
                        .filter(emp => selectedEmployees.includes(emp.id) && emp.status === 'active')
                        .length}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeView === 'periods' && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">{t('payroll.payroll_periods', 'Payroll Periods')}</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowCreatePeriodModal(true)}
                  className="btn btn-sm btn-success"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {t('payroll.create_period', 'Create Period')}
                </button>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('payroll.period', 'Period')}</th>
                    <th>{t('payroll.date_range', 'Date Range')}</th>
                    <th>{t('payroll.employee_count', 'Employee Count')}</th>
                    <th>{t('payroll.total_payable', 'Total Payable')}</th>
                    <th>{t('common.status', t('common.status'))}</th>
                    <th>{t('common.actions', t('common.actions'))}</th>
                  </tr>
                </thead>
                <tbody>
                  {payrollPeriods.map((period, index) => (
                    <tr key={`table-${period.id}-${index}`}>
                      <td className="font-medium">{period.year}-{String(period.month).padStart(2, '0')}</td>
                      <td>
                        {formatDate(period.startDate)} - {formatDate(period.endDate)}
                      </td>
                      <td>{period.employeeCount || 0}</td>
                      <td>{formatCurrency(period.totalPayable || 0)}</td>
                      <td>{getStatusBadge(period.status)}</td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewPeriod(period)}
                            className="btn btn-sm btn-secondary"
                            title={t('common.view', t('common.view'))}
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          
                          {(period.status === 'draft' || period.status === 'accrued') && (
                            <button
                              onClick={() => {
                                if (period && period.startDate && period.endDate) {
                                  setSelectedPeriod(period);
                                  setShowProcessPayrollModal(true);
                                }
                              }}
                              className="btn btn-sm btn-primary"
                              title={period.status === 'draft' 
                                ? t('payroll.process_payroll', 'Process Payroll')
                                : t('payroll.process_salary', 'Process Salary')
                              }
                            >
                              <PlayIcon className="w-4 h-4" />
                            </button>
                          )}
                          
                          {/* Status Change Dropdown */}
                          <select
                            value={period.status}
                            onChange={(e) => handlePeriodStatusChange(period.id, e.target.value)}
                            className="form-select text-xs py-1 px-2"
                            title={t('payroll.change_status', 'Change Status')}
                          >
                            <option value="draft">{t('payroll.status.draft', 'Draft')}</option>
                            <option value="accrued">{t('payroll.status.accrued', 'Accrued')}</option>
                            <option value="paid">{t('payroll.status.paid', t('common.paid'))}</option>
                            <option value="cancelled">{t('payroll.status.cancelled', 'Cancelled')}</option>
                          </select>
                          
                          <button
                            onClick={() => handleDeletePeriod(period.id)}
                            className="btn btn-sm btn-danger"
                            title={t('common.delete', t('common.delete'))}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeView === 'entries' && selectedPeriod && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">
                {t('payroll.payroll_entries', 'Payroll Entries')} - {selectedPeriod.year}-{String(selectedPeriod.month).padStart(2, '0')}
              </h3>
              <div className="flex items-center space-x-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-select"
                >
                  <option value="all">{t('common.all_statuses', 'All Statuses')}</option>
                  <option value="accrued">{t('payroll.status.accrued', 'Accrued')}</option>
                  <option value="paid">{t('payroll.status.paid', t('common.paid'))}</option>
                </select>
                <div className="relative">
                  <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('common.search', 'Search...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEntries(filteredEntries.filter(entry => entry.paymentStatus === 'accrued').map(entry => entry.id));
                          } else {
                            setSelectedEntries([]);
                          }
                        }}
                        checked={selectedEntries.length > 0 && selectedEntries.length === filteredEntries.filter(entry => entry.paymentStatus === 'accrued').length}
                      />
                    </th>
                    <th>{t('payroll.employee_name', 'Employee Name')}</th>
                    <th>{t('payroll.role', 'Role')}</th>
                    <th>{t('payroll.salary', 'Salary')}</th>
                    <th>{t('payroll.commission', 'Commission')}</th>
                    <th>{t('payroll.bonus', 'Bonus')}</th>
                    <th>{t('payroll.total_due', 'Total Due')}</th>
                    <th>{t('payroll.payment_status', 'Payment Status')}</th>
                    <th>{t('common.actions', t('common.actions'))}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry, index) => (
                    <tr key={`entry-${entry.id}-${index}`}>
                      <td>
                        {entry.paymentStatus === 'accrued' && (
                          <input
                            type="checkbox"
                            checked={selectedEntries.includes(entry.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedEntries([...selectedEntries, entry.id]);
                              } else {
                                setSelectedEntries(selectedEntries.filter(id => id !== entry.id));
                              }
                            }}
                          />
                        )}
                      </td>
                      <td className="font-medium">{entry.employeeName}</td>
                      <td>{entry.employeeRole}</td>
                      <td>{formatCurrency(entry.salary || 0)}</td>
                      <td>{formatCurrency(entry.commission || 0)}</td>
                      <td>{formatCurrency(entry.bonus || 0)}</td>
                      <td className="font-medium">{formatCurrency(entry.totalDue || 0)}</td>
                      <td>{getPaymentStatusBadge(entry.paymentStatus)}</td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedEntry(entry);
                              setShowDetailModal(true);
                            }}
                            className="btn btn-sm btn-secondary"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          {entry.paymentStatus === 'accrued' && (
                            <button
                              onClick={() => {
                                setSelectedEntry(entry);
                                setShowAdjustmentModal(true);
                              }}
                              className="btn btn-sm btn-warning"
                            >
                              <AdjustmentsHorizontalIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <CreatePayrollPeriodModal
        isOpen={showCreatePeriodModal}
        onClose={() => setShowCreatePeriodModal(false)}
        onSuccess={handlePeriodCreated}
      />

      <ProcessPayrollModal
        isOpen={showProcessPayrollModal}
        onClose={() => setShowProcessPayrollModal(false)}
        onSuccess={handlePayrollProcessed}
        period={selectedPeriod}
      />

      <PayrollPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentProcessed}
        entryIds={selectedEntries}
        entries={payrollEntries.filter(entry => selectedEntries.includes(entry.id))}
      />

      <PayrollAdjustmentModal
        isOpen={showAdjustmentModal}
        onClose={() => setShowAdjustmentModal(false)}
        onSuccess={handleAdjustmentMade}
        entry={selectedEntry}
      />

      <PayrollDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        entry={selectedEntry}
      />

      <EmployeeManagementModal
        isOpen={showEmployeeManagementModal}
        onClose={() => setShowEmployeeManagementModal(false)}
        onSuccess={handleEmployeeUpdated}
        employee={selectedEmployee}
      />

      <AddEmployeeModal
        isOpen={showAddEmployeeModal}
        onClose={() => setShowAddEmployeeModal(false)}
        onSuccess={handleEmployeeUpdated}
      />
    </div>
  );
};

export default PayrollTab;